// ============================================================
//  AVIRA PRO ERP — Canonical Theme (LOCKED 2 May 2026)
//  Single source of truth — every page must import from here.
//  Pankaj sir's strict rule: NO per-page custom themes.
//
//  15 May 2026: merged in former ZENTRIX `src/theme.ts` so we
//  have ONE canonical theme file. Old src/theme.ts retired.
// ============================================================
import React, { CSSProperties } from "react";
import { formatSelectedCompanyNumber } from "../utils/companyContext";

// ── Color palette (unified) ────────────────────────────────
export const COLORS = {
  // Brand / action
  primary:      "#1D4ED8",   // Primary button (blue)
  secondary:    "#F97316",   // Accent (orange — active tab underline, secondary CTAs)
  accent:       "#F97316",   // alias

  // Header / structural
  headerBg:     "#2D3748",   // Dark navy — page header card
  headerText:   "#FFFFFF",
  headerSub:    "rgba(255,255,255,0.75)",

  // Backgrounds
  pageBg:       "#F0F2F5",   // Page-level background
  cardBg:       "#FFFFFF",
  sectionHead:  "#F7F8FA",   // Table head, sub-section bg
  inputBg:      "#FFFFFF",
  rowHover:     "#F8FAFC",
  rowAlt:       "#FAFBFC",
  white:        "#FFFFFF",

  // Borders / dividers
  border:       "#DDE1E7",
  borderStrong: "#CBD5E1",
  divider:      "#E2E8F0",

  // Text
  text:         "#1A202C",   // Primary
  textMuted:    "#64748B",   // Muted
  textLight:    "#94A3B8",
  subText:      "#64748B",   // alias of textMuted

  // Status / semantic
  green:        "#16A34A",   // alias of success
  red:          "#DC2626",   // alias of danger
  amber:        "#F59E0B",
  success:      "#16A34A",
  successBg:    "#D1FAE5",
  successText:  "#065F46",
  danger:       "#DC2626",
  dangerBg:     "#FEF2F2",
  dangerText:   "#991B1B",
  warning:      "#D97706",
  warn:         "#D97706",   // alias of warning (used by pricing module)
  warningBg:    "#FEF3C7",
  warningText:  "#92400E",
  info:         "#0891B2",
  infoBg:       "#DBEAFE",
  infoText:     "#1E40AF",

  // Type badges
  customerBg:   "#DBEAFE",
  customerText: "#1E40AF",
  vendorBg:     "#D1FAE5",
  vendorText:   "#065F46",

  // Sparingly-used accents
  purple:       "#7C3AED",
  purpleBg:     "#EDE9FE",
  purpleText:   "#5B21B6",
  teal:         "#0F766E",
  indigo:       "#6366F1",

  // Enterprise UI aliases used by older modules. Keep these centralized so
  // legacy dashboard/list pages can be brought under the same design system.
  blue:         "#2563EB",
  blueL:        "#EFF6FF",
  blueBorder:   "#BFDBFE",
  greenL:       "#ECFDF5",
  greenBorder:  "#6EE7B7",
  purpleL:      "#F5F3FF",
  purpleBorder: "#DDD6FE",
  orange:       "#D97706",
  orangeL:      "#FFFBEB",
  orangeBorder: "#FDE68A",
  redL:         "#FEF2F2",
  redBorder:    "#FCA5A5",
  tealL:        "#ECFEFF",
  tealBorder:   "#A5F3FC",
  indigoL:      "#EEF2FF",
  pink:         "#DB2777",
  pinkL:        "#FDF2F8",
  cyan:         "#0E7490",
  cyanL:        "#ECFEFF",
  gray:         "#374151",
  grayL:        "#F9FAFB",
  grayBorder:   "#E5E7EB",
  bg:           "#EEF2F7",
  sub:          "#64748B",

  // Legacy key aliases for modules that pre-date the central design system.
  card:         "#FFFFFF",
  surface:      "#FFFFFF",
  muted:        "#64748B",
  panelBg:      "#F7F8FA",
  canvasBg:     "#FFFFFF",
  pageShadow:   "0 2px 12px rgba(0,0,0,.08)",
  selected:     "#1D4ED8",
  primaryBg:    "#DBEAFE",
  primaryDark:  "#0F1F3A",
  critical:     "#DC2626",
  criticalBg:   "#FEF2F2",
  criticalBr:   "#FCA5A5",
  warningBr:    "#FDE68A",
  infoBr:       "#BFDBFE",
  successBr:    "#6EE7B7",
  dangerBr:     "#FCA5A5",
  dangerLight:  "#FEE2E2",
  redLight:     "#FEE2E2",
  redDark:      "#991B1B",
  greenLight:   "#D1FAE5",
  greenDark:    "#065F46",
  blueLight:    "#DBEAFE",
  blueDark:     "#1E40AF",
  amberLight:   "#FEF3C7",
  amberDark:    "#92400E",
  purpleLight:  "#F3E8FF",
  purpleDark:   "#5B21B6",
  grayLight:    "#F3F4F6",
  tealBg:       "#CFFAFE",
  purpleBgSoft: "#F3E8FF",
  gold:         "#EAB308",
  goldBg:       "#FEF9C3",
  grayBg:       "#F8FAFC",
  chip:         "#EFF6FF",
};

// `C` is the historical name used by 119 pages — same object.
export const C = COLORS;
export const UI_COLORS = COLORS;

// ── Spacing / Radius / Font ────────────────────────────────
export const SPACING = { xs: 4, sm: 8, md: 12, lg: 16, xl: 20, xxl: 24 };
export const RADIUS  = { xs: 3, sm: 4, md: 6, lg: 8, xl: 8, pill: 20 };
export const FONT    = {
  family: "'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif",
  xs: 10, sm: 11, base: 12, md: 13, lg: 14, xl: 16, xxl: 18, h1: 20,
};

// Central enterprise UI contract. Any project-wide UI change should begin here
// and in src/theme/designSystem.css.
export const DESIGN = {
  shell: {
    sidebarFull: 220,
    sidebarMini: 58,
    topbarHeight: 48,
    tabbarHeight: 38,
    breadcrumbHeight: 40,
  },
  density: {
    pagePadding: "16px",
    cardPadding: "14px",
    toolbarGap: "8px",
    tableCellY: "8px",
    tableCellX: "10px",
    controlHeight: 30,
    rowActionHeight: 26,
  },
  radius: {
    control: RADIUS.sm,
    card: RADIUS.lg,
    section: RADIUS.lg,
    badge: RADIUS.pill,
  },
  shadow: {
    card: "0 1px 3px rgba(15,23,42,0.08)",
    raised: "0 8px 24px rgba(15,23,42,0.12)",
  },
  motion: {
    fast: "120ms ease",
    normal: "180ms ease",
  },
};

export const CSS_VARS: Record<string, string | number> = {
  "--erp-primary": COLORS.primary,
  "--erp-secondary": COLORS.secondary,
  "--erp-accent": COLORS.accent,
  "--erp-header-bg": COLORS.headerBg,
  "--erp-page-bg": COLORS.pageBg,
  "--erp-card-bg": COLORS.cardBg,
  "--erp-section-bg": COLORS.sectionHead,
  "--erp-input-bg": COLORS.inputBg,
  "--erp-row-hover": COLORS.rowHover,
  "--erp-border": COLORS.border,
  "--erp-border-strong": COLORS.borderStrong,
  "--erp-divider": COLORS.divider,
  "--erp-text": COLORS.text,
  "--erp-muted": COLORS.textMuted,
  "--erp-light": COLORS.textLight,
  "--erp-success": COLORS.success,
  "--erp-danger": COLORS.danger,
  "--erp-warning": COLORS.warning,
  "--erp-info": COLORS.info,
  "--erp-font": FONT.family,
  "--erp-font-xs": `${FONT.xs}px`,
  "--erp-font-sm": `${FONT.sm}px`,
  "--erp-font-base": `${FONT.base}px`,
  "--erp-font-md": `${FONT.md}px`,
  "--erp-font-lg": `${FONT.lg}px`,
  "--erp-radius-control": `${DESIGN.radius.control}px`,
  "--erp-radius-card": `${DESIGN.radius.card}px`,
  "--erp-radius-section": `${DESIGN.radius.section}px`,
  "--erp-radius-badge": `${DESIGN.radius.badge}px`,
  "--erp-control-height": `${DESIGN.density.controlHeight}px`,
  "--erp-row-action-height": `${DESIGN.density.rowActionHeight}px`,
  "--erp-card-shadow": DESIGN.shadow.card,
  "--erp-raised-shadow": DESIGN.shadow.raised,
  "--erp-topbar-height": `${DESIGN.shell.topbarHeight}px`,
  "--erp-tabbar-height": `${DESIGN.shell.tabbarHeight}px`,
  "--erp-breadcrumb-height": `${DESIGN.shell.breadcrumbHeight}px`,
};

export const cssVarsStyle = CSS_VARS as React.CSSProperties;

// ============================================================
//  Lightweight inline style helpers (pre-existing API)
// ============================================================
export const inputStyle: React.CSSProperties = {
  padding: "6px 10px", borderRadius: DESIGN.radius.control, border: `1px solid ${C.borderStrong}`,
  fontSize: FONT.base, background: C.inputBg, color: C.text, minWidth: 140,
  outline: "none",
};

export const dateInput: React.CSSProperties = { ...inputStyle, minWidth: 0 };

export const lbl: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: C.subText,
};

export const btn: React.CSSProperties = {
  padding: "6px 14px", borderRadius: DESIGN.radius.control, border: "none",
  fontSize: FONT.base, fontWeight: 600, cursor: "pointer",
};

export const btnSm = (bg: string): React.CSSProperties => ({
  padding: "3px 9px", borderRadius: DESIGN.radius.control, border: "none",
  fontSize: FONT.sm, fontWeight: 600, cursor: "pointer", background: bg, color: "#fff",
});

export const th: React.CSSProperties = {
  padding: "8px 10px", textAlign: "left", fontWeight: 600, fontSize: 11, color: C.text,
};
export const thR: React.CSSProperties = { ...th, textAlign: "right" };
export const thC: React.CSSProperties = { ...th, textAlign: "center" };

export const td: React.CSSProperties = {
  padding: "6px 10px", verticalAlign: "middle", color: C.text, fontSize: 12,
};
export const tdR: React.CSSProperties = { ...td, textAlign: "right" };
export const tdC: React.CSSProperties = { ...td, textAlign: "center" };

export const badge = (color: string): React.CSSProperties => ({
  display: "inline-block",
  background: `${color}22`, color, padding: "2px 8px",
  borderRadius: DESIGN.radius.badge, fontSize: FONT.sm, fontWeight: 700,
});

// Status badge variants
export const STATUS_BADGE: Record<string, { c: string; bg: string }> = {
  DRAFT:     { c: "#92400E", bg: "#FEF3C7" },
  POSTED:    { c: "#065F46", bg: "#D1FAE5" },
  CANCELLED: { c: "#991B1B", bg: "#FEE2E2" },
  APPROVED:  { c: "#1E40AF", bg: "#DBEAFE" },
  REJECTED:  { c: "#991B1B", bg: "#FEE2E2" },
};

// Item type / category color map (battery industry)
export const ITEM_TYPE_COLOR: Record<string, { color: string; bg: string }> = {
  "Raw Material":   { color: "#065F46", bg: "#D1FAE5" },
  "Finished Good":  { color: "#1E40AF", bg: "#DBEAFE" },
  "Plate FG":       { color: "#9333EA", bg: "#F3E8FF" },
  "Semi Finished":  { color: "#92400E", bg: "#FEF3C7" },
  "Consumable":     { color: "#6D28D9", bg: "#EDE9FE" },
  "Packaging":      { color: "#0891B2", bg: "#CFFAFE" },
  // Legacy underscore variants
  RAW_MATERIAL:  { color: "#065F46", bg: "#D1FAE5" },
  WIP:           { color: "#92400E", bg: "#FEF3C7" },
  FINISHED_GOOD: { color: "#1E40AF", bg: "#DBEAFE" },
  CONSUMABLE:    { color: "#6D28D9", bg: "#EDE9FE" },
  TRADING:       { color: "#9D174D", bg: "#FCE7F3" },
};

// ============================================================
//  Rich component-style helpers (T.*) — merged from old theme.ts
//  Use these when you need consistent SAP-B1-style components.
// ============================================================
export const T = {
  // Page wrapper
  page: {
    fontFamily: FONT.family,
    background: COLORS.pageBg,
    minHeight: "calc(100vh - 100px)",
    padding: SPACING.lg,
  } as CSSProperties,

  // Header card (dark navy with icon + title + subtitle)
  pageHeader: {
    background: COLORS.headerBg,
    color: COLORS.headerText,
    padding: "12px 20px",
    borderRadius: `${RADIUS.md}px ${RADIUS.md}px 0 0`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap" as const,
    gap: SPACING.md,
  } as CSSProperties,

  pageTitle: { fontSize: FONT.xl, fontWeight: 700, margin: 0 } as CSSProperties,

  pageSubtitle: { fontSize: FONT.sm, opacity: 0.8, marginTop: 2 } as CSSProperties,

  // Tabs strip directly below header (continuous with header card)
  tabsStrip: {
    background: COLORS.cardBg,
    border: `1px solid ${COLORS.border}`,
    borderTop: "none",
    display: "flex",
    overflowX: "auto" as const,
  } as CSSProperties,

  tab: (active: boolean): CSSProperties => ({
    padding: "10px 20px",
    border: "none",
    background: active ? COLORS.cardBg : COLORS.sectionHead,
    borderBottom: active ? `3px solid ${COLORS.secondary}` : `1px solid ${COLORS.border}`,
    fontWeight: 600,
    fontSize: FONT.md,
    cursor: "pointer",
    color: active ? COLORS.secondary : COLORS.textMuted,
    whiteSpace: "nowrap",
    transition: "all 0.15s",
  }),

  // Card / panel — continuous with tabs strip (no top border)
  panel: {
    background: COLORS.cardBg,
    border: `1px solid ${COLORS.border}`,
    borderTop: "none",
    padding: SPACING.lg,
  } as CSSProperties,

  // Standalone card (outside header-tab combo)
  card: {
    background: COLORS.cardBg,
    border: `1px solid ${COLORS.border}`,
    borderRadius: RADIUS.md,
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  } as CSSProperties,

  // Buttons (variant-driven)
  btn: (variant: "primary" | "secondary" | "success" | "danger" | "neutral" | "outline" = "primary"): CSSProperties => {
    const variants = {
      primary:   { bg: COLORS.primary,   color: "#fff" },
      secondary: { bg: COLORS.secondary, color: "#fff" },
      success:   { bg: COLORS.success,   color: "#fff" },
      danger:    { bg: COLORS.danger,    color: "#fff" },
      neutral:   { bg: COLORS.textMuted, color: "#fff" },
      outline:   { bg: "#fff",           color: COLORS.text },
    };
    const v = variants[variant];
    return {
      background: v.bg,
      color: v.color,
      border: variant === "outline" ? `1px solid ${COLORS.borderStrong}` : "none",
      borderRadius: RADIUS.sm,
      padding: "7px 14px",
      fontSize: FONT.base,
      fontWeight: 600,
      cursor: "pointer",
      transition: "filter 0.12s",
    };
  },

  btnSm: (variant: "primary" | "secondary" | "success" | "danger" | "neutral" | "outline" = "primary"): CSSProperties => {
    const variants = {
      primary:   { bg: COLORS.primary,   color: "#fff" },
      secondary: { bg: COLORS.secondary, color: "#fff" },
      success:   { bg: COLORS.success,   color: "#fff" },
      danger:    { bg: COLORS.danger,    color: "#fff" },
      neutral:   { bg: COLORS.textMuted, color: "#fff" },
      outline:   { bg: "#fff",           color: COLORS.text },
    };
    const v = variants[variant];
    return {
      background: v.bg,
      color: v.color,
      border: variant === "outline" ? `1px solid ${COLORS.borderStrong}` : "none",
      borderRadius: 3,
      padding: "3px 10px",
      fontSize: FONT.sm,
      fontWeight: 600,
      cursor: "pointer",
    };
  },

  // Inputs
  input: (err?: boolean): CSSProperties => ({
    width: "100%",
    padding: "7px 10px",
    border: `1px solid ${err ? COLORS.danger : COLORS.borderStrong}`,
    borderRadius: RADIUS.sm,
    fontSize: FONT.md,
    outline: "none",
    boxSizing: "border-box",
    background: COLORS.inputBg,
  }),

  select: {
    width: "100%",
    padding: "7px 10px",
    border: `1px solid ${COLORS.borderStrong}`,
    borderRadius: RADIUS.sm,
    fontSize: FONT.md,
    outline: "none",
    background: COLORS.inputBg,
    boxSizing: "border-box",
  } as CSSProperties,

  label: {
    fontSize: FONT.base,
    color: COLORS.text,
    fontWeight: 700,
    marginBottom: 4,
    display: "block",
  } as CSSProperties,

  labelSm: {
    fontSize: FONT.sm,
    color: COLORS.textMuted,
    fontWeight: 600,
    marginBottom: 4,
    display: "block",
  } as CSSProperties,

  errMsg: { fontSize: FONT.sm, color: COLORS.danger, marginTop: 2 } as CSSProperties,

  // Tables
  th: {
    padding: "8px 10px",
    textAlign: "left" as const,
    fontWeight: 600,
    fontSize: FONT.sm,
    color: COLORS.text,
    textTransform: "uppercase" as const,
    background: COLORS.sectionHead,
    borderBottom: `2px solid ${COLORS.border}`,
  } as CSSProperties,

  td: {
    padding: "8px 10px",
    fontSize: FONT.base,
    color: COLORS.text,
    borderBottom: `1px solid ${COLORS.border}`,
  } as CSSProperties,

  // Section heading inside panel
  secHead: {
    fontSize: FONT.md,
    fontWeight: 700,
    color: COLORS.text,
    marginBottom: SPACING.md,
    paddingBottom: SPACING.xs + 2,
    borderBottom: `2px solid ${COLORS.divider}`,
  } as CSSProperties,

  // KPI / stat card
  statCard: (accent: string = COLORS.primary): CSSProperties => ({
    background: COLORS.cardBg,
    borderRadius: RADIUS.lg,
    padding: "12px 16px",
    border: `1px solid ${COLORS.border}`,
    borderLeft: `4px solid ${accent}`,
    boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
  }),

  statLabel: {
    fontSize: FONT.sm,
    color: COLORS.textMuted,
    fontWeight: 700,
    textTransform: "uppercase" as const,
    letterSpacing: 0.3,
  } as CSSProperties,

  statValue: (color: string = COLORS.text): CSSProperties => ({
    fontSize: FONT.h1,
    fontWeight: 800,
    color,
    marginTop: 4,
  }),

  // Badges (semantic-named variants)
  badge: (kind: "customer" | "vendor" | "active" | "inactive" | "warning" | "info" | "purple"): CSSProperties => {
    const map: Record<string, { bg: string; color: string }> = {
      customer: { bg: COLORS.customerBg, color: COLORS.customerText },
      vendor:   { bg: COLORS.vendorBg,   color: COLORS.vendorText },
      active:   { bg: COLORS.successBg, color: COLORS.successText },
      inactive: { bg: COLORS.dangerBg,  color: COLORS.dangerText },
      warning:  { bg: COLORS.warningBg, color: COLORS.warningText },
      info:     { bg: COLORS.infoBg,    color: COLORS.infoText },
      purple:   { bg: COLORS.purpleBg,  color: COLORS.purpleText },
    };
    const c = map[kind];
    return {
      display: "inline-block",
      padding: "2px 10px",
      borderRadius: RADIUS.pill,
      fontSize: FONT.sm,
      fontWeight: 700,
      background: c.bg,
      color: c.color,
    };
  },

  // Modal
  modalBg: {
    position: "fixed" as const,
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  } as CSSProperties,

  modalBox: {
    background: COLORS.cardBg,
    borderRadius: RADIUS.md,
    maxHeight: "92vh",
    overflowY: "auto" as const,
  } as CSSProperties,

  modalHeader: {
    background: COLORS.headerBg,
    color: COLORS.headerText,
    padding: "12px 18px",
    fontWeight: 700,
    fontSize: FONT.lg,
  } as CSSProperties,

  modalFooter: {
    padding: SPACING.md,
    background: COLORS.sectionHead,
    borderTop: `1px solid ${COLORS.border}`,
    display: "flex",
    justifyContent: "flex-end",
    gap: SPACING.sm,
  } as CSSProperties,
};

// ============================================================
//  Formatters
// ============================================================
export const fmtCurrency = (n: any, currencyCode?: string) => {
  const code = String(currencyCode || "").trim().toUpperCase();
  const amount = formatSelectedCompanyNumber(n, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return code ? `${code} ${amount}` : amount;
};

export const fmtNumber = (n: any) =>
  formatSelectedCompanyNumber(n, { minimumFractionDigits: 3, maximumFractionDigits: 3 });

export const fmtINR = (n: any): string =>
  formatSelectedCompanyNumber(n, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtCurrencyShort = (n: any, currencyCode?: string): string => {
  const v = Number(n || 0);
  const prefix = String(currencyCode || "").trim().toUpperCase();
  const value = formatSelectedCompanyNumber(v, {
    notation: "compact",
    maximumFractionDigits: 2,
  });
  return prefix ? `${prefix} ${value}` : value;
};

// Backward-compatible export for older modules; new code should use the
// currency-neutral name above.
export const fmtINRShort = fmtCurrencyShort;

// Default export = T (so `import T from "../../theme/colors"` keeps working)
export default T;
